import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import PropTypes from "prop-types";
import "./IdleTimeoutManager.css";
import { cookieAttributes } from "util/resumeCookieSettings";

const DEFAULT_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_WARNING_DURATION_MS = 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 1000;
const TIMER_CHECK_INTERVAL_MS = 1000;

const getDuration = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const IDLE_TIMEOUT_MS = getDuration(
  process.env.REACT_APP_IDLE_TIMEOUT_MS,
  DEFAULT_IDLE_TIMEOUT_MS
);

const WARNING_DURATION_MS = Math.min(
  getDuration(
    process.env.REACT_APP_IDLE_WARNING_MS,
    DEFAULT_WARNING_DURATION_MS
  ),
  IDLE_TIMEOUT_MS
);

const WARNING_THRESHOLD_MS = IDLE_TIMEOUT_MS - WARNING_DURATION_MS;

const getEnvironmentName = () => {
  const hostname = String(window.location.hostname || "").toLowerCase();

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "local";
  }

  if (hostname.includes("-dev.")) {
    return "dev";
  }

  if (hostname.includes("-qa.")) {
    return "qa";
  }

  if (hostname.includes("-uat.")) {
    return "uat";
  }

  return "prod";
};

const LAST_ACTIVITY_COOKIE_NAME =
  `platform_idle_last_activity_${getEnvironmentName()}`;

const readLastActivity = () => {
  const prefix = `${LAST_ACTIVITY_COOKIE_NAME}=`;
  const cookies = String(document.cookie || "").split(";");

  for (let index = 0; index < cookies.length; index += 1) {
    const cookie = cookies[index].trim();

    if (cookie.indexOf(prefix) === 0) {
      const timestamp = Number(cookie.substring(prefix.length));

      return Number.isFinite(timestamp) && timestamp > 0
        ? timestamp
        : null;
    }
  }

  return null;
};

const writeLastActivity = timestamp => {
  const attributes = cookieAttributes();

  const cookieParts = [
    `${LAST_ACTIVITY_COOKIE_NAME}=${timestamp}`,
    "path=/",
    `expires=${attributes.expires.toUTCString()}`,
    `SameSite=${attributes.sameSite || "lax"}`
  ];

  if (attributes.domain && attributes.domain !== "localhost") {
    cookieParts.push(`domain=${attributes.domain}`);
  }

  if (window.location.protocol === "https:") {
    cookieParts.push("secure");
  }

  document.cookie = cookieParts.join("; ");
};

export const resetSharedIdleSession = () => {
  writeLastActivity(Date.now());
};

const formatSeconds = seconds => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  const formattedMinutes =
    minutes < 10 ? `0${minutes}` : String(minutes);

  const formattedSeconds =
    remainingSeconds < 10
      ? `0${remainingSeconds}`
      : String(remainingSeconds);

  return `${formattedMinutes}:${formattedSeconds}`;
};

const IdleTimeoutManager = ({ enabled, onTimeout }) => {
  const [warningOpen, setWarningOpen] = useState(false);

  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.ceil(WARNING_DURATION_MS / 1000)
  );

  const warningOpenRef = useRef(false);
  const timeoutStartedRef = useRef(false);
  const lastActivityWriteRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const setWarningState = useCallback(open => {
    warningOpenRef.current = open;
    setWarningOpen(open);
  }, []);

  const evaluateIdleState = useCallback(
    (now = Date.now()) => {
      if (!enabled) {
        return;
      }

      const lastActivity = readLastActivity();

      if (!lastActivity) {
        writeLastActivity(now);
        lastActivityWriteRef.current = now;
        timeoutStartedRef.current = false;
        setWarningState(false);
        return;
      }

      const elapsed = Math.max(0, now - lastActivity);
      const remaining = IDLE_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        setWarningState(true);
        setSecondsRemaining(0);

        if (!timeoutStartedRef.current) {
          timeoutStartedRef.current = true;
          onTimeoutRef.current();
        }

        return;
      }

      timeoutStartedRef.current = false;

      if (elapsed >= WARNING_THRESHOLD_MS) {
        setWarningState(true);
        setSecondsRemaining(Math.ceil(remaining / 1000));
        return;
      }

      setWarningState(false);
      setSecondsRemaining(
        Math.ceil(WARNING_DURATION_MS / 1000)
      );
    },
    [enabled, setWarningState]
  );

  const continueSession = useCallback(() => {
    if (!enabled) {
      return;
    }

    const now = Date.now();

    writeLastActivity(now);
    lastActivityWriteRef.current = now;
    timeoutStartedRef.current = false;

    setWarningState(false);
    setSecondsRemaining(
      Math.ceil(WARNING_DURATION_MS / 1000)
    );
  }, [enabled, setWarningState]);

  useEffect(() => {
    if (!enabled) {
      timeoutStartedRef.current = false;
      setWarningState(false);
      return undefined;
    }

    const handleActivity = () => {
      if (document.hidden) {
        return;
      }

      const now = Date.now();
      const lastActivity = readLastActivity();

      const elapsed = lastActivity
        ? Math.max(0, now - lastActivity)
        : 0;

      if (
        warningOpenRef.current ||
        (lastActivity && elapsed >= WARNING_THRESHOLD_MS)
      ) {
        evaluateIdleState(now);
        return;
      }

      if (
        now - lastActivityWriteRef.current <
        ACTIVITY_WRITE_THROTTLE_MS
      ) {
        return;
      }

      writeLastActivity(now);
      lastActivityWriteRef.current = now;
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        evaluateIdleState();
        handleActivity();
      }
    };

    const handlePageShow = () => {
      evaluateIdleState();
      handleActivity();
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click"
    ];

    evaluateIdleState();
    handleActivity();

    activityEvents.forEach(eventName => {
      window.addEventListener(
        eventName,
        handleActivity,
        true
      );
    });

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    window.addEventListener("focus", handleActivity);
    window.addEventListener("pageshow", handlePageShow);

    const timer = window.setInterval(
      evaluateIdleState,
      TIMER_CHECK_INTERVAL_MS
    );

    return () => {
      window.clearInterval(timer);

      activityEvents.forEach(eventName => {
        window.removeEventListener(
          eventName,
          handleActivity,
          true
        );
      });

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      window.removeEventListener("focus", handleActivity);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [enabled, evaluateIdleState, setWarningState]);

  return (
    <Modal
      show={warningOpen}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      dialogClassName="idle-timeout-warning-dialog"
    >
      <Modal.Header>
        <Modal.Title>Inactivity Warning</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p aria-live="polite">
          You will be automatically logged off in{" "}
          {formatSeconds(secondsRemaining)} due to inactivity.
          Would you like to continue?
        </p>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary idle-timeout-continue-button"
          onClick={continueSession}
        >
          Continue
        </button>
      </Modal.Footer>
    </Modal>
  );
};

IdleTimeoutManager.propTypes = {
  enabled: PropTypes.bool,
  onTimeout: PropTypes.func.isRequired
};

IdleTimeoutManager.defaultProps = {
  enabled: false
};

export default IdleTimeoutManager;
